import React, { useState } from 'react';
import { ThreeDots, TailSpin } from "react-loader-spinner";
import Select from "react-select";
import { addArtWorks } from "../../Api/apiUrls";

const Add_Art_Work = ({ onClose, onArtworkAdded, instructorId }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '', // Added description field
    rate: '',
    sequence: '',
    status: '0', // Default to Pending
    photo: [] // Will now contain only one image
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [previewImage, setPreviewImage] = useState(null); // Changed to singular

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Allow only numbers for rate and sequence
    if (name === 'rate' || name === 'sequence') {
      if (/^\d*$/.test(value)) {
        setFormData({
          ...formData,
          [name]: value
        });
      }
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleStatusChange = (selectedOption) => {
    setFormData({
      ...formData,
      status: selectedOption.value
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0]; // Get only the first file
    
    if (!file) return;
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewImage(reader.result);
    };
    reader.readAsDataURL(file);
    
    // Update form data with single file
    setFormData({
      ...formData,
      photo: [file] // Store as array with single element
    });
  };

  const removeImage = () => {
    setFormData({
      ...formData,
      photo: []
    });
    setPreviewImage(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.photo.length === 0) {
      alert("Please select an image");
      return;
    }
    
    if (!formData.title.trim()) {
      alert("Please enter artwork title");
      return;
    }
    
    setIsSubmitting(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("user", instructorId);
      formDataToSend.append("title", formData.title);
      formDataToSend.append("description", formData.description || ""); // Added description
      formDataToSend.append("rate", formData.rate || "0");
      formDataToSend.append("sequence", formData.sequence || "0");
      formDataToSend.append("status", formData.status);

      // Append the single photo
      formData.photo.forEach(file => {
        formDataToSend.append("photo", file);
      });

      const response = await fetch(addArtWorks, {
        method: "POST",
        body: formDataToSend,
      });

      if (!response.ok) {
        throw new Error("Failed to add artwork");
      }

      const data = await response.json();
      
      if (data.success) {
        alert("Artwork added successfully!");
        if (onArtworkAdded) onArtworkAdded();
        onClose();
      } else {
        alert(data.message || "Failed to add artwork");
      }
    } catch (error) {
      console.error("Error adding artwork:", error);
      alert("Error adding artwork. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const StatusOptions = [
    { value: "0", label: "Pending" },
    { value: "1", label: "Approve" },
    { value: "-1", label: "Reject" },
  ];

  const handleClick = (e) => {
    if (e.currentTarget === e.target) {
      onClose();
    }
  };

  return (
    <div className="popup-background" onClick={handleClick}>
      <div className="popup" style={{ maxWidth: "600px", maxHeight: "90vh" }}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Add New Artwork</h5>
            <button type="button" className="close" onClick={onClose}>
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          
          <div className="modal-body">
            <form onSubmit={handleSubmit}>
              {/* Image Upload - Single Image */}
              <div className="form-group mb-3">
                <label className="small mb-1">Artwork Image *</label>
                <input
                  type="file"
                  className="form-control"
                  accept="image/*"
                  onChange={handleFileChange}
                  required={formData.photo.length === 0}
                  // Remove 'multiple' attribute to allow single selection only
                />
                <small className="text-muted">Upload one image only</small>
                
                {/* Single Image Preview */}
                {previewImage && (
                  <div className="mt-3">
                    <label className="small mb-2">Selected Image:</label>
                    <div className="row">
                      <div className="col-6">
                        <div className="position-relative">
                          <img
                            src={previewImage}
                            alt="Preview"
                            style={{
                              width: "100%",
                              height: "200px",
                              objectFit: "cover",
                              borderRadius: "5px",
                              border: "1px solid #ddd"
                            }}
                          />
                          <button
                            type="button"
                            className="btn btn-sm btn-danger position-absolute top-0 end-0"
                            style={{ transform: 'translate(50%, -50%)' }}
                            onClick={removeImage}
                          >
                            <i className="bi bi-x"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Title */}
              <div className="form-group mb-3">
                <label className="small mb-1">Title *</label>
                <input
                  type="text"
                  className="form-control"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Enter artwork title"
                  required
                />
              </div>

              {/* Description - New Field */}
              <div className="form-group mb-3">
                <label className="small mb-1">Description (Optional)</label>
                <textarea
                  className="form-control"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Enter artwork description"
                  rows="3"
                />
              </div>

              {/* Rate */}
              <div className="form-group mb-3">
                <label className="small mb-1">Rate (Optional)</label>
                <input
                  type="text"
                  className="form-control"
                  name="rate"
                  value={formData.rate}
                  onChange={handleInputChange}
                  placeholder="Enter rate"
                  inputMode="numeric"
                />
              </div>

              {/* Sequence */}
              <div className="form-group mb-3">
                <label className="small mb-1">Sequence (Optional)</label>
                <input
                  type="text"
                  className="form-control"
                  name="sequence"
                  value={formData.sequence}
                  onChange={handleInputChange}
                  placeholder="Enter sequence"
                  inputMode="numeric"
                />
              </div>

              {/* Status */}
              <div className="form-group mb-3">
                <label className="small mb-1">Status</label>
                <Select
                  options={StatusOptions}
                  value={StatusOptions.find(opt => opt.value === formData.status)}
                  onChange={handleStatusChange}
                  placeholder="Select status"
                />
              </div>

              {/* Buttons */}
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={onClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <ThreeDots color="#fff" height={20} width={20} />
                      <span className="ms-2">Adding...</span>
                    </>
                  ) : (
                    'Add Artwork'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Add_Art_Work;